import { useEffect, useMemo, useRef } from "react";
import { Container } from "react-bootstrap";
import { UseFormReturn, useWatch } from "react-hook-form";
import { useRouteContext } from "@tanstack/react-router";

import DateInput from "@/components/shared/forms/DateInput";
import FormFieldRow from "@/components/shared/forms/FormFieldRow";
import RadioSelect from "@/components/shared/forms/RadioSelect";
import ReadOnlySelect from "@/components/shared/forms/ReadOnlySelect";
import TextInput from "@/components/shared/forms/TextInput";
import { type LANG } from "@/models.ts";
import { useCoreData } from "@/queries.ts";
import { FaBriefcase } from "react-icons/fa";
import { MdLanguage } from "react-icons/md";

import { useLANGFormLogic } from "../../useLANGFormLogic";
import { LanguageMultiSelect } from "@/components/shared/LanguageMultiSelect";

type LanguageOption = {
  Text: string;
  Value: string;
};

type PreschoolSummary = {
  PreschoolCDSCode: string;
  PreschoolName: string;
};

type LANGWizardFormProps = {
  form: UseFormReturn<LANG, any, LANG>;
  preschools: PreschoolSummary[];
};

export function LANGWizardForm({ form, preschools }: LANGWizardFormProps) {
  const leaContext = useRouteContext({ from: "/$leaid" });
  const tooltipMap = leaContext.coreData.tooltipMap.get("LANG");
  const coreData = useCoreData();

  const {
    register,
    control,
    setValue,
    clearErrors,
    trigger,
    watch,
    getValues,
    formState: { errors },
  } = form;

  const resetKey = "wizard-new-lang";

  const formValues = watch();
  const lang = {
    ...formValues,
  };

  const instrumentOptOut = useWatch({
    control,
    name: "InstrumentOptOut" as any,
  }) as string | undefined;

  const dllInstrumentDesignated = useWatch({
    control,
    name: "DLLInstrumentDesignated" as any,
  }) as string | undefined;

  const dllTeacherDesignated = useWatch({
    control,
    name: "DLLTeacherDesignated" as any,
  }) as string | undefined;

  const instrumentDate = useWatch({
    control,
    name: "InstrumentDate" as any,
  }) as string | undefined;

  const dllTeacherDesignatedDate = useWatch({
    control,
    name: "DLLTeacherDesignatedDate" as any,
  }) as string | undefined;

  const prevDllInstrumentDesignatedRef = useRef<string | undefined>(undefined);

  const languagesOptions = useMemo(() => {
    return (coreData.languagesList ?? []) as LanguageOption[];
  }, [coreData.languagesList]);

  const langLogic = useLANGFormLogic({
    form,
    lang,
    languagesOptions,
    resetKey,
    instrumentOptOut,
  });

  useEffect(() => {
    register("InstrumentLanguagesHeard1" as any, {
      validate: (v: any) => {
        if (instrumentOptOut === "Y") {
          return v == null || String(v).trim().length === 0
            ? true
            : 'Instrument Languages Heard must be blank when Instrument Opt Out = "Y"';
        }

        if (instrumentOptOut === "N") {
          const heard = v;
          const spoken = getValues("InstrumentLanguagesSpoken1" as any) as
            | string
            | undefined;
          const understood = getValues(
            "InstrumentLanguagesUnderstood1" as any
          ) as string | undefined;

          const hasAny =
            (heard && String(heard).trim().length > 0) ||
            (spoken && String(spoken).trim().length > 0) ||
            (understood && String(understood).trim().length > 0);

          if (!hasAny) {
            return "Instrument Opt Out must be 'Y' when no Instrument Language Heard, Spoken, or Understood are filled out";
          }
        }

        return true;
      },
    });
  }, [register, instrumentOptOut, getValues]);

  useEffect(() => {
    register("InstrumentLanguagesUnderstood1" as any, {
      validate: (v: any) => {
        if (instrumentOptOut === "Y") {
          return v == null || String(v).trim().length === 0
            ? true
            : 'Instrument Languages Understood must be blank when Instrument Opt Out = "Y"';
        }
        return true;
      },
    });
  }, [register, instrumentOptOut]);

  useEffect(() => {
    register("InstrumentLanguagesSpoken1" as any, {
      validate: (v: any) => {
        if (instrumentOptOut === "Y") {
          return v == null || String(v).trim().length === 0
            ? true
            : 'Instrument Languages Spoken must be blank when Instrument Opt Out = "Y"';
        }
        return true;
      },
    });
  }, [register, instrumentOptOut]);

  useEffect(() => {
    const prev = prevDllInstrumentDesignatedRef.current;
    prevDllInstrumentDesignatedRef.current = dllInstrumentDesignated;

    if (dllInstrumentDesignated === "Y") {
      setValue("DLLTeacherDesignated" as any, "N", {
        shouldDirty: true,
        shouldValidate: true,
      });
      return;
    }

    if (dllInstrumentDesignated === "N" && prev === "Y") {
      setValue("DLLTeacherDesignated" as any, undefined, {
        shouldDirty: true,
        shouldValidate: true,
      });
      clearErrors("DLLTeacherDesignated" as any);
    }
  }, [dllInstrumentDesignated, setValue, clearErrors]);

  useEffect(() => {
    if (instrumentOptOut === "Y") {
      setValue("InstrumentDate" as any, "", { shouldDirty: true });
      clearErrors("InstrumentDate" as any);
      clearErrors("InstrumentLanguagesHeard1" as any);
      clearErrors("InstrumentLanguagesUnderstood1" as any);
      clearErrors("InstrumentLanguagesSpoken1" as any);
    }
  }, [instrumentOptOut, setValue, clearErrors]);

  useEffect(() => {
    if (dllTeacherDesignated === "N") {
      setValue("DLLTeacherDesignatedDate" as any, "", { shouldDirty: true });
      clearErrors("DLLTeacherDesignatedDate" as any);
    }
  }, [dllTeacherDesignated, setValue, clearErrors]);

  useEffect(() => {
    if (instrumentDate && dllTeacherDesignatedDate) {
      trigger("DLLTeacherDesignatedDate" as any);
    }
  }, [instrumentDate, dllTeacherDesignatedDate, trigger]);

  const isHeardDisabled = instrumentOptOut === "Y";
  const isUnderstoodDisabled = instrumentOptOut === "Y";
  const isSpokenDisabled = instrumentOptOut === "Y";
  const isInstrumentDateDisabled = instrumentOptOut === "Y";
  const isDllTeacherDesignatedDisabled = dllInstrumentDesignated === "Y";
  const isDllTeacherDesignatedDateDisabled = dllTeacherDesignated === "N";

  return (
    <Container
      className="ps-3 pe-3 overview-container"
      style={{ borderTop: "none", borderRadius: 0 }}
    >
      <div className="form-card-modern mb-3">
        <div className="section-header">
          <FaBriefcase className="section-icon" />
          <h3 className="section-title">Agency Information</h3>
        </div>

        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="CSPPID"
              labelText="CSPPID"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  readOnly
                  maxLength={50}
                  {...register(name)}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="EffectiveStartDate"
              labelText="Effective Start Date"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <DateInput
                  id={id}
                  max="9999-12-31"
                  disabled={false}
                  {...register(name, {
                    validate: langLogic.validateEffectiveStartDate,
                  })}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="ProgramYear"
              labelText="Program Year"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <ReadOnlySelect
                  id={id}
                  control={control}
                  name={name}
                  rules={{ required: "Program Year is required" }}
                  options={coreData.programYearList}
                />
              )}
            </FormFieldRow>
          </div>
        </div>

        <div className="row g-3">
          <div className="col-md-6">
            <FormFieldRow
              name="PreschoolCode"
              labelText="Preschool"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <ReadOnlySelect
                  id={id}
                  control={control}
                  name={name}
                  rules={{ required: "Preschool is required" }}
                  options={preschools.map((x) => ({
                    Text: x.PreschoolName,
                    Value: x.PreschoolCDSCode,
                  }))}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-6">
            <FormFieldRow
              name="LocalChildId"
              labelText="Local Child ID (CICN)"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  readOnly
                  maxLength={50}
                  {...register(name, {
                    required: "Local Child ID (CICN) is required",
                  })}
                />
              )}
            </FormFieldRow>
          </div>
        </div>
      </div>

      <div className="form-card-modern mb-3">
        <div className="section-header">
          <MdLanguage className="section-icon" />
          <h3 className="section-title">Language Information</h3>
        </div>

        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="InstrumentOptOut"
              labelText="Instrument Opt Out"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
              labelAs="legend"
            >
              {(id, name) => (
                <RadioSelect
                  id={id}
                  disabled={false}
                  {...register(name, {
                    required: "Instrument Opt Out is required",
                  })}
                  options={[
                    { Text: "Yes", Value: "Y" },
                    { Text: "No", Value: "N" },
                  ]}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="InstrumentDate"
              labelText="Instrument Date"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired={instrumentOptOut === "N"}
            >
              {(id, name) => (
                <DateInput
                  id={id}
                  disabled={isInstrumentDateDisabled}
                  max="9999-12-31"
                  {...register(name, {
                    validate: langLogic.validateInstrumentDate,
                  })}
                />
              )}
            </FormFieldRow>
          </div>
        </div>

        <br />

        <h4>Interview Languages</h4>
        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="InstrumentLanguagesHeard1"
              labelText="Language(s) Heard"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id, name, validationProps) => (
                <div
                  className={isHeardDisabled ? "kendo-ms-disabled" : undefined}
                >
                  <LanguageMultiSelect
                    id={id}
                    ariaLabel="Language(s) Heard"
                    ariaDescribedBy={validationProps["aria-describedby"]}
                    disabled={isHeardDisabled}
                    invalid={!!validationProps["aria-invalid"]}
                    data={languagesOptions}
                    value={langLogic.heard.value}
                    onChange={(items) => {
                      langLogic.heard.onChange(items);
                      trigger("InstrumentLanguagesHeard1" as any);
                    }}
                  />
                </div>
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="InstrumentLanguagesUnderstood1"
              labelText="Language(s) Understood"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id, name, validationProps) => (
                <div
                  className={
                    isUnderstoodDisabled ? "kendo-ms-disabled" : undefined
                  }
                >
                  <LanguageMultiSelect
                    id={id}
                    ariaLabel="Language(s) Understood"
                    ariaDescribedBy={validationProps["aria-describedby"]}
                    disabled={isUnderstoodDisabled}
                    invalid={!!validationProps["aria-invalid"]}
                    data={languagesOptions}
                    value={langLogic.understood.value}
                    onChange={(items) => {
                      langLogic.understood.onChange(items);
                      trigger([
                        "InstrumentLanguagesUnderstood1",
                        "InstrumentLanguagesHeard1",
                      ] as any);
                    }}
                  />
                </div>
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="InstrumentLanguagesSpoken1"
              labelText="Language(s) Spoken"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id, name, validationProps) => (
                <div
                  className={isSpokenDisabled ? "kendo-ms-disabled" : undefined}
                >
                  <LanguageMultiSelect
                    id={id}
                    ariaLabel="Language(s) Spoken"
                    ariaDescribedBy={validationProps["aria-describedby"]}
                    disabled={isSpokenDisabled}
                    invalid={!!validationProps["aria-invalid"]}
                    data={languagesOptions}
                    value={langLogic.spoken.value}
                    onChange={(items) => {
                      langLogic.spoken.onChange(items);
                      trigger([
                        "InstrumentLanguagesSpoken1",
                        "InstrumentLanguagesHeard1",
                      ] as any);
                    }}
                  />
                </div>
              )}
            </FormFieldRow>
          </div>
        </div>

        <br />

        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="DLLInstrumentDesignated"
              labelText="DLL Instrument Designated"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired={instrumentOptOut === "Y"}
              labelAs="legend"
            >
              {(id, name) => (
                <RadioSelect
                  id={id}
                  disabled={false}
                  {...register(name, {
                    validate: (v: any) => {
                      if (instrumentOptOut === "Y") {
                        return v === "Y" || v === "N"
                          ? true
                          : "DLL Instrument Designated is required";
                      }
                      return true;
                    },
                  })}
                  options={[
                    { Text: "Yes", Value: "Y" },
                    { Text: "No", Value: "N" },
                  ]}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="DLLTeacherDesignated"
              labelText="DLL Teacher Designated"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              labelAs="legend"
            >
              {(id, name) => (
                <RadioSelect
                  id={id}
                  disabled={isDllTeacherDesignatedDisabled}
                  {...register(name, {
                    validate: (v: any) => {
                      if (dllInstrumentDesignated === "Y") {
                        return v === "N"
                          ? true
                          : 'DLL Teacher Designated must be "N" when DLL Instrument Designated = "Y"';
                      }
                      return true;
                    },
                  })}
                  options={[
                    { Text: "Yes", Value: "Y" },
                    { Text: "No", Value: "N" },
                  ]}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="DLLTeacherDesignatedDate"
              labelText="DLL Teacher Designated Date"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired={dllTeacherDesignated === "Y"}
            >
              {(id, name) => (
                <DateInput
                  id={id}
                  disabled={isDllTeacherDesignatedDateDisabled}
                  max="9999-12-31"
                  {...register(name, {
                    validate: (v: any) =>
                      langLogic.validateDLLTeacherDesignatedDate(
                        v,
                        dllTeacherDesignated
                      ),
                  })}
                />
              )}
            </FormFieldRow>
          </div>
        </div>
      </div>
    </Container>
  );
}
